import { z } from 'zod';
import { emailSchema, orgRoleSchema } from './auth';
import { objectIdSchema } from './common';
import { grantNodeSchema } from './orgs';

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();
export const locationSchema = z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) });
export type GeoLocation = z.infer<typeof locationSchema>;

// ---- Fields ----
export const fieldInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  code: optionalText(32),
  country: optionalText(64),
  operator: optionalText(120),
  location: locationSchema.nullable().optional(),
  notes: optionalText(2000),
});
export type FieldInput = z.infer<typeof fieldInputSchema>;
export const fieldSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  code: z.string().nullable(),
  country: z.string().nullable(),
  operator: z.string().nullable(),
  location: locationSchema.nullable(),
  notes: z.string().nullable(),
});
export type Field = z.infer<typeof fieldSchema>;

// ---- Well status (wellsites were removed: a well carries its own status) ----
export const WELL_STATUSES = ['testing', 'standby', 'rigup', 'offline', 'demob'] as const;
export const wellStatusSchema = z.enum(WELL_STATUSES);
export type WellStatus = z.infer<typeof wellStatusSchema>;

// ---- Wells ----
export const WELL_TYPES = ['oil', 'gas', 'water_injector', 'other'] as const;
export const completionSchema = z.object({
  tubingSize: optionalText(32),
  perfTopMd: z.number().min(0).max(20000).nullable().optional(),
  perfBottomMd: z.number().min(0).max(20000).nullable().optional(),
  reservoir: optionalText(120),
});
export const wellInputSchema = z.object({
  fieldId: objectIdSchema,
  name: z.string().trim().min(1).max(120),
  uwi: optionalText(64),
  status: wellStatusSchema.default('standby'),
  location: locationSchema.nullable().optional(),
  timeZone: optionalText(64),
  type: z.enum(WELL_TYPES).default('oil'),
  completion: completionSchema.nullable().optional(),
  notes: optionalText(2000),
});
export type WellInput = z.infer<typeof wellInputSchema>;
export const wellSchema = z.object({
  id: objectIdSchema,
  fieldId: objectIdSchema,
  name: z.string(),
  uwi: z.string().nullable(),
  status: wellStatusSchema,
  location: locationSchema.nullable(),
  timeZone: z.string().nullable(),
  type: z.enum(WELL_TYPES),
  completion: completionSchema.nullable(),
  defaultDashboardId: objectIdSchema.nullable(),
  defaultSchematicId: objectIdSchema.nullable(),
  notes: z.string().nullable(),
});
export type Well = z.infer<typeof wellSchema>;

/** Finds wells by field and well name (case-insensitive), creating the missing ones. */
export const ensureWellsRequestSchema = z.object({
  wells: z
    .array(z.object({ field: z.string().trim().min(1).max(120), well: z.string().trim().min(1).max(120) }))
    .min(1)
    .max(500),
});
export type EnsureWellsRequest = z.infer<typeof ensureWellsRequestSchema>;
export const ensureWellsResponseSchema = z.object({
  items: z.array(z.object({ field: z.string(), well: z.string(), fieldId: objectIdSchema, wellId: objectIdSchema, createdField: z.boolean(), createdWell: z.boolean() })),
});
export type EnsureWellsResponse = z.infer<typeof ensureWellsResponseSchema>;

// ---- Tests, flow periods, sequence log ----
/** The kind of operation a test records (well testing is one kind, not detailed further). */
export const TEST_TYPES = ['well_testing', 'drilling', 'cementing', 'coiled_tubing', 'production_plant', 'other'] as const;
export type TestType = (typeof TEST_TYPES)[number];
/** Kinds whose oil, gas and water rates add up on the home page. */
export const PRODUCING_TEST_TYPES: readonly TestType[] = ['well_testing', 'production_plant'];
/** The detailed well-test types used before 2026-11-01 are read as well testing. */
const LEGACY_WELL_TEST = new Set(['flowback', 'cleanup', 'dst', 'production', 'buildup']);
const testTypeSchema = z.preprocess((v) => (typeof v === 'string' && LEGACY_WELL_TEST.has(v) ? 'well_testing' : v), z.enum(TEST_TYPES));
export const TEST_STATUSES = ['planned', 'running', 'completed', 'cancelled'] as const;
const isoDate = z.string().datetime({ offset: true });

export const flowPeriodInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  startAt: isoDate,
  endAt: isoDate.nullable().optional(),
  choke: z.object({ value: z.number().min(0).max(10000), unitId: z.string().max(32) }).nullable().optional(),
  notes: optionalText(1000),
});
export type FlowPeriodInput = z.infer<typeof flowPeriodInputSchema>;
export const flowPeriodSchema = z.object({
  id: z.string(),
  name: z.string(),
  startAt: z.string(),
  endAt: z.string().nullable(),
  choke: z.object({ value: z.number(), unitId: z.string() }).nullable(),
  notes: z.string().nullable(),
});
export type FlowPeriod = z.infer<typeof flowPeriodSchema>;

export const testInputSchema = z.object({
  wellId: objectIdSchema,
  name: z.string().trim().min(1).max(120),
  type: testTypeSchema.default('well_testing'),
  status: z.enum(TEST_STATUSES).default('planned'),
  startAt: isoDate,
  endAt: isoDate.nullable().optional(),
});
export type TestInput = z.infer<typeof testInputSchema>;
export const testSchema = z.object({
  id: objectIdSchema,
  wellId: objectIdSchema,
  fieldId: objectIdSchema,
  name: z.string(),
  type: z.enum(TEST_TYPES),
  status: z.enum(TEST_STATUSES),
  startAt: z.string(),
  endAt: z.string().nullable(),
  flowPeriods: z.array(flowPeriodSchema),
  defaultDashboardId: objectIdSchema.nullable(),
  defaultSchematicId: objectIdSchema.nullable(),
});
export type WellTest = z.infer<typeof testSchema>;

export const SEQLOG_CATEGORIES = ['event', 'choke', 'sample', 'safety', 'note'] as const;
export const seqLogInputSchema = z.object({
  at: isoDate,
  text: z.string().trim().min(1).max(1000),
  category: z.enum(SEQLOG_CATEGORIES).default('event'),
});
export type SeqLogInput = z.infer<typeof seqLogInputSchema>;
export const seqLogEntrySchema = z.object({
  /** An entry's id; `logger:<key>` for an event read from SmarDac Logger (read-only). */
  id: z.string(),
  testId: objectIdSchema,
  wellId: objectIdSchema,
  at: z.string(),
  text: z.string(),
  category: z.enum(SEQLOG_CATEGORIES),
  authorName: z.string(),
  /** Written in TwinLink, or an event of SmarDac Logger read live from the data source. */
  source: z.enum(['twinlink', 'logger']).default('twinlink'),
  /** The Logger's own event type (Operator, Alarm …) for a Logger event. */
  sourceType: z.string().nullable().default(null),
  /** Written in the web app, or from a supervisor's WhatsApp message (doc 14). */
  channel: z.enum(['web', 'whatsapp']).default('web'),
  /** WhatsApp: what the supervisor wrote, before the AI tidied it. */
  originalText: z.string().nullable().default(null),
  /** A question to the supervisors is waiting for an answer about this moment. */
  pending: z.boolean().default(false),
});
export type SeqLogEntry = z.infer<typeof seqLogEntrySchema>;

// ---- Equipment ----
export const FLUIDS = ['multiphase', 'oil', 'gas', 'water', 'relief', 'chemical', 'utility', 'any'] as const;
export const portSchema = z.object({
  id: z.string().regex(/^[\w-]{1,32}$/),
  name: z.string().max(64),
  side: z.enum(['left', 'right', 'top', 'bottom']),
  offset: z.number().min(0).max(1),
  fluid: z.enum(FLUIDS),
  dir: z.enum(['in', 'out', 'bidir']),
});
export const signalRoleSchema = z.object({
  role: z.string().regex(/^[a-z][a-z0-9_]{0,47}$/),
  label: z.string().max(80),
  quantity: z.string().max(64),
  defaultUnitId: z.string().max(32),
});
export const equipmentTypeSchema = z.object({
  id: objectIdSchema,
  /** Null for the global library. */
  orgId: objectIdSchema.nullable(),
  key: z.string(),
  name: z.string(),
  category: z.string(),
  size: z.object({ w: z.number(), h: z.number() }),
  ports: z.array(portSchema),
  signalRoles: z.array(signalRoleSchema),
  libraryVersion: z.number().nullable(),
  /** Sanitized SVG of a custom stencil; null for the built-in symbols. */
  symbolSvg: z.string().nullable(),
});
export type EquipmentType = z.infer<typeof equipmentTypeSchema>;
export type EquipmentPort = z.infer<typeof portSchema>;
export type SignalRole = z.infer<typeof signalRoleSchema>;

export const equipmentTypeInputSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,47}$/, 'Use lowercase letters, digits and underscores'),
  name: z.string().trim().min(1).max(120),
  category: z.string().max(64),
  size: z.object({ w: z.number().min(10).max(1000), h: z.number().min(10).max(1000) }),
  ports: z.array(portSchema).max(24).default([]),
  signalRoles: z.array(signalRoleSchema).max(64).default([]),
  /** Custom stencil (doc 07 §6). Sanitized by the server before it is stored. */
  symbolSvg: z.string().max(200_000).nullable().optional(),
});
export type EquipmentTypeInput = z.infer<typeof equipmentTypeInputSchema>;

export const equipmentInputSchema = z.object({
  typeKey: z.string().max(48),
  tag: z.string().trim().min(1).max(64),
  serialNumber: optionalText(64),
  description: optionalText(500),
  wellId: objectIdSchema.nullable().optional(),
  testId: objectIdSchema.nullable().optional(),
  properties: z.record(z.string().max(48), z.union([z.string().max(200), z.number()])).default({}),
});
export type EquipmentInput = z.infer<typeof equipmentInputSchema>;
export const equipmentSchema = z.object({
  id: objectIdSchema,
  typeKey: z.string(),
  typeName: z.string(),
  tag: z.string(),
  serialNumber: z.string().nullable(),
  description: z.string().nullable(),
  wellId: objectIdSchema.nullable(),
  testId: objectIdSchema.nullable(),
  properties: z.record(z.union([z.string(), z.number()])),
});
export type Equipment = z.infer<typeof equipmentSchema>;

// ---- Tree ----
export const treeWellSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  type: z.enum(WELL_TYPES),
  status: wellStatusSchema,
  location: locationSchema.nullable(),
  runningTest: z.object({ id: objectIdSchema, name: z.string(), type: z.enum(TEST_TYPES) }).nullable(),
});
export const treeFieldSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  /** False when the user only sees the name because one of its wells is granted. */
  granted: z.boolean(),
  wells: z.array(treeWellSchema),
});
export const treeResponseSchema = z.object({ fields: z.array(treeFieldSchema) });
export type AssetTree = z.infer<typeof treeResponseSchema>;
export type TreeField = z.infer<typeof treeFieldSchema>;
export type TreeWell = z.infer<typeof treeWellSchema>;

// ---- Grants & access review ----
export const grantsResponseSchema = z.object({ grants: z.array(grantNodeSchema) });
export const putGrantsRequestSchema = z.object({
  grants: z.array(grantNodeSchema).max(500),
  /** Required to give a Client a whole field (doc 02 §3.5). */
  confirmClientFieldGrant: z.boolean().optional(),
});
export type PutGrantsRequest = z.infer<typeof putGrantsRequestSchema>;

export const accessQuerySchema = grantNodeSchema;
export const accessEntrySchema = z.object({
  userId: objectIdSchema,
  name: z.string(),
  email: z.string(),
  role: orgRoleSchema,
  /** `role` = implicit (Org Admin sees everything); `grant` = through the grant named below. */
  via: z.enum(['role', 'grant']),
  grant: z.object({ nodeType: z.enum(['field', 'well']), nodeId: objectIdSchema, nodeName: z.string() }).nullable(),
});
export type AccessEntry = z.infer<typeof accessEntrySchema>;

// ---- CSV imports ----
export const importRowResultSchema = z.object({
  line: z.number(),
  status: z.enum(['ok', 'skip', 'error']),
  message: z.string(),
});
export const importResultSchema = z.object({
  dryRun: z.boolean(),
  created: z.number(),
  skipped: z.number(),
  errors: z.number(),
  rows: z.array(importRowResultSchema),
});
export type ImportResult = z.infer<typeof importResultSchema>;
export const csvImportRequestSchema = z.object({ csv: z.string().min(1).max(500_000) });

export const memberImportRowSchema = z.object({
  email: emailSchema,
  name: z.string().max(120).optional(),
  role: orgRoleSchema,
  grants: z.array(z.string()).default([]),
});
